import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import moment from 'moment';
import Badge from 'components/v2/Badge';
import CircleButton from 'components/v2/buttons/CircleButton';
import EditButton from 'components/v2/buttons/EditButton';
import ExclamationIcon from 'components/v2/icons/ExclamationIcon';
import ExternalLinkIcon from 'components/v2/icons/ExternalLinkIcon';
import { get } from 'lodash';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import { NEXT_ACTIONS_STATUS, NEXT_ACTION_COLOR } from 'constants/next-actions';
import { titleize } from 'underscore.string';
import { unescapeSpecialChars } from '../../../helpers/helper_functions';
import { COMPLETE_STOP } from '../../../constants/next-actions';
import { getTimezone } from '../../../helpers/stops';
import ExternalLink from '../../v2/ExternalLink';

export default function NextAction({
  action,
  nextAction,
  actionType,
  isCustomerView,
  onNextActionClick,
  hideEdit,
}) {
  const { t } = useTranslation();
  const status = NEXT_ACTIONS_STATUS[action.action];
  const shouldShowShipper =
    !isCustomerView && get(action, 'shipment.shipper._id', false);

  function shouldShowCarrier() {
    if (isCustomerView) {
      return false;
    }
    return !!action?.shipment?.carrier?._id;
  }

  function handleNextActionClick() {
    onNextActionClick(action);
  }

  function getEquipmentIds(action) {
    if (action.shipment.equipments) return action.shipment.equipments;
    if (action.nextStop?.container?.equipment_id)
      return [action.nextStop.container.equipment_id];
    return [];
  }

  function formatDate(date) {
    if (!date) {
      return '';
    }
    return moment(date)
      .tz(getTimezone(action.nextStop))
      .format('L LT');
  }
  const equipmentIds = getEquipmentIds(action);
  return (
    <div className="p-4">
      <div className="sm:-mx-2 flex flex-wrap">
        <div className="sm:px-2 mb-2 sm:mb-0">
          <Badge
            className="w-40"
            color={NEXT_ACTION_COLOR[status]}
            onClick={handleNextActionClick}
          >
            {t(status)}
          </Badge>
        </div>
        <div className="sm:px-2 mb-2 sm:mb-0">
          <DescriptionListItem label={t('SHIPMENT_ID')} className="mb-2">
            <div className="text-sm w-40">
              <a
                className="underline"
                href={`/shipments/${action.shipment._id}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                <div className="flex items-center space-x-1">
                  <span>{action.shipment.shipment_id}</span>
                  <ExternalLinkIcon className="w-4 h-4" />
                </div>
              </a>
            </div>
          </DescriptionListItem>
        </div>
        <div className="sm:px-2 mb-2 sm:mb-0">
          <DescriptionListItem label={t('CONTAINER_HASH')} className="mb-2">
            {equipmentIds.map((equipmentId) => (
              <div className="text-sm w-40">{equipmentId}</div>
            ))}
          </DescriptionListItem>
        </div>
        {shouldShowShipper && (
          <div className="w-48 mb-2 sm:mb-0 sm:px-2">
            <DescriptionListItem
              label={titleize(t('SHIPPER', { count: 1 }))}
              className="mb-2"
            >
              <div className="flex items-center space-x-1 underline">
                <ExternalLink
                  className="w-4 h-4"
                  href={`/users/${action.shipment.shipper._id}`}
                  title={unescapeSpecialChars(
                    action.shipment.shipper.company_name,
                  )}
                  showIcon
                />
              </div>
            </DescriptionListItem>
          </div>
        )}
        {!isCustomerView && (
          <div className="w-48 mb-2 sm:mb-0 sm:px-2">
            {shouldShowCarrier() && (
              <DescriptionListItem
                label={titleize(t('CARRIER', { count: 1 }))}
                className="mb-2"
              >
                <a
                  href={`/users/${action.shipment.carrier._id}`}
                  className="underline"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <div className="flex items-center space-x-1">
                    <span>
                      {unescapeSpecialChars(action.shipment.carrier.name)}
                    </span>
                    <ExternalLinkIcon className="w-4 h-4" />
                  </div>
                </a>
              </DescriptionListItem>
            )}
          </div>
        )}
        <div className="sm:px-2 flex-1 mb-2 sm:mb-2">
          <DescriptionListItem label={t('NEXT_ACTION')} className="mb-2">
            <div className="text-sm">
              <p
                className={classnames({
                  'font-medium': !!action.subActionMessage,
                })}
              >
                {t(nextAction)}
              </p>
              {action.subActionMessage && (
                <p className="italic">{t(action.subActionMessage)}</p>
              )}
            </div>
          </DescriptionListItem>
        </div>
        {COMPLETE_STOP === actionType && (
          <div className="sm:px-2 flex-1 mb-2 sm:mb-2">
            <DescriptionListItem label={t('APPT_SCHEDULED')} className="mb-2">
              <span className="text-sm w-40">
                {formatDate(action.nextStop?.window_end)}
              </span>
            </DescriptionListItem>
          </div>
        )}
        {!hideEdit && (
          <div className="sm:px-2">
            <EditButton
              type="button"
              size="small"
              onClick={handleNextActionClick}
              title={t(action.action)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
